import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { useOrderByParams } from '~/hooks/order-by';
import CreateChannelGuideIllustration from '~/images/illustrations/create-channel-guide.svg';
import type { ChannelGuideList__ChannelGuide as ChannelGuide } from '../../queries/list.generated';
import { useColumns } from '../lib/columns';

const NoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      illustration={CreateChannelGuideIllustration}
      header={t('pages.channel_guides.empty_state.header')}
      description={t('pages.channel_guides.empty_state.description')}
    />
  );
};

export interface TableProps {
  channelGuides: readonly ChannelGuide[];
  loading: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const Table = ({ channelGuides, loading, onCheck, selectedIds }: TableProps) => {
  const columns = useColumns();
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      channels: !isSmallAndDown,
      shows: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  return (
    <ScrollableDataGrid
      checkboxSelection={!isSmallAndDown}
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      isRowSelectable={({ row }) => row.canDestroy.value}
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      onRowSelectionModelChange={onCheck}
      onRowClick={({ row }: GridRowParams<ChannelGuide>) => navigate(String(row.id))}
      rows={channelGuides}
      rowSelectionModel={selectedIds}
      slots={{
        baseCheckbox: ListCheckbox,
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
